import { addMinutes, isAfter, isBefore, set } from 'date-fns'
import { Color } from 'zens-storybook'

import type { NormalizedCompanyColorScheme } from './colorscheme'
import { Trigger } from '../data'
import { Mode as ScheduleMode, VisualProgram as ScheduleVisualProgram } from '../data/schedule'

export function programStatus(
  program: ScheduleVisualProgram,
  index: number,
  types?: string[],
) {
  const value = program?.[Math.floor(index / 16)]
  const valueIndex = 15 - (index % 16)
  const status = (value >> (valueIndex * 2)) & 3
  return status === 2 && isTypeEnabled(types, ['cleaning']) ? ScheduleMode.Clean : undefined
}

// Currently we can set only a "cleaning" schedule
// which corresponds to status 2 in the schedule program.
// The code for "blocked" status (value 1) is left at the backend for future use.
export function programColorsForDay(
  date: Date,
  schedule: ScheduleVisualProgram | undefined,
  triggers: Trigger[] | undefined,
  colorScheme: NormalizedCompanyColorScheme,
  types?: string[],
) {
  const colors = new Array(48).fill('').map((_, index) => {
    const slotStatus = schedule ? statusForSlot(schedule, index) : undefined
    const startTime = set(date, {
      hours: Math.floor(index / 2),
      minutes: (index % 2) * 30,
      seconds: 0,
      milliseconds: 0,
    })
    const endTime = addMinutes(startTime, 30)

    // Schedule slots have higher priority than triggers.
    if (slotStatus === 2) {
      for (const [label, colorName] of Object.entries(colorScheme.schedule_colors)) {
        if (!isTypeEnabled(types, [label])) continue
        const color = colorForName(colorName)
        if (color) return color
      }
    }

    let selected:
      | {
          color: string
          priority: number
        }
      | undefined
    triggers?.forEach((trigger) => {
      if (!(isAfter(endTime, trigger.startTime) && isBefore(startTime, trigger.endTime))) return
      const colorName = colorNameForTrigger(trigger)
      const color = colorForName(colorName)
      if (!color) return
      const label = labelForColor(colorName, colorScheme.trigger_colors)
      // Only apply trigger type filters when the color can be mapped to a known label.
      if (label && !isTypeEnabled(types, [label])) return
      const priority = priorityForTrigger(trigger)
      if (!selected || priority < selected.priority) selected = { color, priority }
    })
    return selected?.color || ''
  })

  return colors
}

function colorForName(colorName: string) {
  return Color[colorName as keyof typeof Color] || ''
}

function colorNameForTrigger(trigger: Trigger) {
  const data = trigger as Trigger & {
    colorName?: string
    type?: string
    data?: { color?: string | number }
  }
  return (data.colorName || data.type || data.data?.color || '').toString().toLowerCase()
}

function labelForColor(colorName: string, colors?: Record<string, string>) {
  if (!colorName || !colors) return undefined
  return Object.entries(colors).find(([_, color]) => color === colorName)?.[0]
}

function priorityForTrigger(trigger: Trigger) {
  const data = trigger as Trigger & { priority?: number; data?: { prio?: number } }
  if (Number.isFinite(data.priority)) return data.priority as number
  if (Number.isFinite(data.data?.prio)) return data.data?.prio as number
  return Number.MAX_SAFE_INTEGER
}

function statusForSlot(program: ScheduleVisualProgram, index: number) {
  const value = program?.[Math.floor(index / 16)]
  const valueIndex = 15 - (index % 16)
  return (value >> (valueIndex * 2)) & 3
}

function isTypeEnabled(types: string[] | undefined, values: string[]) {
  if (!types) return true
  const selectedTypes = new Set(types)
  return values.some((value) => selectedTypes.has(value))
}

function indexForTime(time: string): number {
  const digits = time?.split(':')
  const hour = parseInt(digits?.[0] || '0', 10) || 0
  const minute = parseInt(digits?.[1] || '0', 10) || 0
  return hour * 2 + Math.floor(minute / 30)
}

export function programColorsForTimes(startTime: string, endTime: string, color: string, disabled?: boolean) {
  const startIndex = indexForTime(startTime)
  const endIndex = indexForTime(endTime) === 0 ? 48 : indexForTime(endTime) - 1
  const colors = new Array(48)
    .fill('')
    .map((_, index) =>
      index >= startIndex && index <= endIndex ? (disabled ? Color.calmGrey : color || Color.lightGreyBlue) : '',
    )

  return colors
}
